import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, InjectionToken, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

export const BD_SEO_BASE_URL = new InjectionToken<string>('BD_SEO_BASE_URL');

@Injectable({
  providedIn: 'root',
})
export class BdSeoService {
  private readonly _meta = inject(Meta);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _document = inject<Document>(DOCUMENT);

  private _canonicalLink: HTMLLinkElement | null = null;
  private readonly _baseUrl: string;

  constructor() {
    const baseUrl = inject(BD_SEO_BASE_URL, { optional: true });

    this._baseUrl = baseUrl ?? '';
    this._listenToRouteChanges();
  }

  setCanonicalPath(path: string): void {
    this.setCanonical(`${this._baseUrl}${path}`);
  }

  setCanonical(url: string): void {
    if (!this._canonicalLink) {
      this._canonicalLink = this._document.createElement('link');
      this._canonicalLink.setAttribute('rel', 'canonical');
      this._document.head.appendChild(this._canonicalLink);
    }
    this._canonicalLink.setAttribute('href', url);
  }

  setRobots(content: string): void {
    const existing = this._meta.getTag('name="robots"');
    if (existing) {
      this._meta.updateTag({ name: 'robots', content });
    } else {
      this._meta.addTag({ name: 'robots', content });
    }
  }

  setDescription(content: string): void {
    const existing = this._meta.getTag('name="description"');
    if (existing) {
      this._meta.updateTag({ name: 'description', content });
    } else {
      this._meta.addTag({ name: 'description', content });
    }
  }

  removeCanonical(): void {
    if (this._canonicalLink) {
      this._canonicalLink.remove();
      this._canonicalLink = null;
    }
  }

  removeRobots(): void {
    this._meta.removeTag('name="robots"');
  }

  removeDescription(): void {
    this._meta.removeTag('name="description"');
  }

  private _listenToRouteChanges(): void {
    this._router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => {
        this.removeCanonical();
        this.removeRobots();
        this.removeDescription();
      });
  }
}
